type StockAnalysisMessages = {
  pageDescription: string;
  analyzeStock: string;
  screenStocks: string;
  tickerPlaceholder: string;
  aShares: string;
  usMarket: string;
  analyzing: string;
  analyze: string;
  runLabel: string;
  analysisResult: string;
  screenCriteriaPlaceholder: string;
  screening: string;
  screeningResults: string;
  researchDisclaimer: string;
  dsaService: string;
  alphaSiftService: string;
  ready: string;
  unavailable: string;
  unknown: string;
  reportBrief: string;
  reportDetailed: string;
  reportFull: string;
  working: string;
  scanMarket: string;
  autoAnalyzeCandidates: string;
  noMarketStrategy: string;
  dataSourceDiagnostics: string;
  dsaReport: string;
  deepResearch: string;
  alphaDisclaimer: string;
  running: string;
  optionalContext: string;
  currentRun: string;
  candidates: string;
  evaluate: string;
  evaluation: string;
  savedRuns: string;
  noCandidates: string;
  rank: string;
  code: string;
  name: string;
  score: string;
  price: string;
  change: string;
  risk: string;
  reason: string;
  actions: string;
  analyzed: string;
  notAnalyzed: string;
  rating: string;
  research: string;
  batchProgress: string;
  failedCount: string;
  batchSubmitted: string;
  summarizingReports: string;
  allAnalysisCompleted: string;
  analysisPending: string;
  researchQuestion: string;
  statusPending: string;
  statusProcessing: string;
  statusCompleted: string;
  statusFailed: string;
};

export type StockAnalysisLocale =
  | 'en-US'
  | 'zh-Hans'
  | 'zh-Hant'
  | 'de'
  | 'ko'
  | 'ja'
  | 'fr'
  | 'ru'
  | 'it'
  | 'ar'
  | 'es'
  | 'hi';

export const stockAnalysisTranslations: Record<
  StockAnalysisLocale,
  StockAnalysisMessages
> = {
  'en-US': {
    pageDescription:
      'Cross-market screening and deep stock research powered by daily_stock_analysis and AlphaSift.',
    analyzeStock: 'Analyze Stock',
    screenStocks: 'Screen Stocks',
    tickerPlaceholder: 'Ticker (e.g. 600519, AAPL)',
    aShares: 'A-Shares',
    usMarket: 'US Market',
    analyzing: 'Analyzing...',
    analyze: 'Analyze',
    runLabel: 'Run',
    analysisResult: 'Analysis Result',
    screenCriteriaPlaceholder: 'Describe screening criteria',
    screening: 'Screening...',
    screeningResults: 'Screening Results',
    researchDisclaimer:
      'Screening results are research candidates, not investment advice. Verify source warnings and the full report before making decisions.',
    dsaService: 'DSA service',
    alphaSiftService: 'AlphaSift',
    ready: 'ready',
    unavailable: 'unavailable',
    unknown: 'unknown',
    reportBrief: 'DSA Brief',
    reportDetailed: 'DSA Detailed',
    reportFull: 'DSA Full',
    working: 'Working...',
    scanMarket: 'Scan Market',
    autoAnalyzeCandidates:
      'Automatically analyze every AlphaSift candidate with daily_stock_analysis',
    noMarketStrategy:
      'No installed AlphaSift strategy supports this market. Start the sidecar with the Hanggent launcher to enable the bundled US strategy.',
    dataSourceDiagnostics: 'Data-source diagnostics',
    dsaReport: 'daily_stock_analysis report',
    deepResearch: 'Deep research',
    alphaDisclaimer:
      'AlphaSift results are research candidates for further review only, not investment advice.',
    running: 'Running...',
    optionalContext: 'Optional research context for AlphaSift',
    currentRun: 'Run {{runId}} - {{count}} candidates',
    candidates: '{{count}} candidates',
    evaluate: 'Evaluate',
    evaluation: 'Evaluation',
    savedRuns: 'Saved Runs',
    noCandidates: 'No candidates returned.',
    rank: 'Rank',
    code: 'Code',
    name: 'Name',
    score: 'Score',
    price: 'Price',
    change: 'Change',
    risk: 'Risk',
    reason: 'Reason',
    actions: 'Actions',
    analyzed: 'Analyzed',
    notAnalyzed: 'Not analyzed',
    rating: 'Score {{score}}',
    research: 'Research',
    batchProgress: 'DSA batch analysis: {{completed}}/{{total}} completed',
    failedCount: ', {{count}} failed',
    batchSubmitted: 'Batch analysis submitted',
    summarizingReports: 'Summarizing individual stock reports',
    allAnalysisCompleted:
      'Completed - AlphaSift screening and DSA analysis of {{count}} stocks finished',
    analysisPending: 'Analysis pending - {{taskId}}',
    researchQuestion:
      'Provide a verifiable deep-research summary covering financial quality, competitive advantages, valuation, major risks, and near-term catalysts.',
    statusPending: 'Pending',
    statusProcessing: 'Processing',
    statusCompleted: 'Completed',
    statusFailed: 'Failed',
  },
  'zh-Hans': {
    pageDescription: '由 daily_stock_analysis 和 AlphaSift 提供跨市场筛选与个股深度研究。',
    analyzeStock: '分析个股',
    screenStocks: '筛选股票',
    tickerPlaceholder: '股票代码（例如 600519、AAPL）',
    aShares: 'A 股',
    usMarket: '美股',
    analyzing: '分析中...',
    analyze: '分析',
    runLabel: '任务',
    analysisResult: '分析结果',
    screenCriteriaPlaceholder: '描述筛选条件',
    screening: '筛选中...',
    screeningResults: '筛选结果',
    researchDisclaimer: '筛选结果仅供研究，不构成投资建议。做出决定前，请核对数据源警告和完整报告。',
    dsaService: 'DSA 服务',
    alphaSiftService: 'AlphaSift',
    ready: '就绪',
    unavailable: '不可用',
    unknown: '未知',
    reportBrief: 'DSA 简报',
    reportDetailed: 'DSA 详细报告',
    reportFull: 'DSA 完整报告',
    working: '处理中...',
    scanMarket: '扫描市场',
    autoAnalyzeCandidates: 'AlphaSift 筛选后自动用 daily_stock_analysis 分析每只候选股',
    noMarketStrategy: '已安装的 AlphaSift 策略均不支持此市场。请使用 Hanggent 启动器运行侧车，以启用内置美股策略。',
    dataSourceDiagnostics: '数据源诊断',
    dsaReport: 'daily_stock_analysis 报告',
    deepResearch: '深度研究',
    alphaDisclaimer: 'AlphaSift 结果仅是供进一步核查的研究候选，不构成投资建议。',
    running: '运行中...',
    optionalContext: '可选的 AlphaSift 研究背景',
    currentRun: '任务 {{runId}} - {{count}} 只候选股',
    candidates: '{{count}} 只候选股',
    evaluate: '评估',
    evaluation: '评估结果',
    savedRuns: '已保存任务',
    noCandidates: '没有返回候选股。',
    rank: '排名',
    code: '代码',
    name: '名称',
    score: '评分',
    price: '价格',
    change: '涨跌',
    risk: '风险',
    reason: '原因',
    actions: '操作',
    analyzed: '已分析',
    notAnalyzed: '未分析',
    rating: '评分 {{score}}',
    research: '研究',
    batchProgress: 'DSA 批量分析：{{completed}}/{{total}} 已完成',
    failedCount: '，{{count}} 个失败',
    batchSubmitted: '批量分析已提交',
    summarizingReports: '正在汇总个股报告',
    allAnalysisCompleted: '已完成 AlphaSift 筛选和 {{count}} 只股票的 DSA 分析',
    analysisPending: '分析等待中 - {{taskId}}',
    researchQuestion: '请综合财务质量、竞争优势、估值、主要风险和近期催化剂，给出可验证的深度研究摘要。',
    statusPending: '等待中',
    statusProcessing: '处理中',
    statusCompleted: '已完成',
    statusFailed: '失败',
  },
  'zh-Hant': {
    pageDescription: '由 daily_stock_analysis 和 AlphaSift 提供跨市場篩選與個股深度研究。',
    analyzeStock: '分析個股',
    screenStocks: '篩選股票',
    tickerPlaceholder: '股票代碼（例如 600519、AAPL）',
    aShares: 'A 股',
    usMarket: '美股',
    analyzing: '分析中...',
    analyze: '分析',
    runLabel: '任務',
    analysisResult: '分析結果',
    screenCriteriaPlaceholder: '描述篩選條件',
    screening: '篩選中...',
    screeningResults: '篩選結果',
    researchDisclaimer: '篩選結果僅供研究，不構成投資建議。做出決定前，請核對資料來源警告和完整報告。',
    dsaService: 'DSA 服務',
    alphaSiftService: 'AlphaSift',
    ready: '就緒',
    unavailable: '不可用',
    unknown: '未知',
    reportBrief: 'DSA 簡報',
    reportDetailed: 'DSA 詳細報告',
    reportFull: 'DSA 完整報告',
    working: '處理中...',
    scanMarket: '掃描市場',
    autoAnalyzeCandidates: 'AlphaSift 篩選後自動用 daily_stock_analysis 分析每隻候選股',
    noMarketStrategy: '已安裝的 AlphaSift 策略均不支援此市場。請使用 Hanggent 啟動器執行側車，以啟用內建美股策略。',
    dataSourceDiagnostics: '資料來源診斷',
    dsaReport: 'daily_stock_analysis 報告',
    deepResearch: '深度研究',
    alphaDisclaimer: 'AlphaSift 結果僅是供進一步核查的研究候選，不構成投資建議。',
    running: '執行中...',
    optionalContext: '可選的 AlphaSift 研究背景',
    currentRun: '任務 {{runId}} - {{count}} 隻候選股',
    candidates: '{{count}} 隻候選股',
    evaluate: '評估',
    evaluation: '評估結果',
    savedRuns: '已儲存任務',
    noCandidates: '沒有傳回候選股。',
    rank: '排名',
    code: '代碼',
    name: '名稱',
    score: '評分',
    price: '價格',
    change: '漲跌',
    risk: '風險',
    reason: '原因',
    actions: '操作',
    analyzed: '已分析',
    notAnalyzed: '未分析',
    rating: '評分 {{score}}',
    research: '研究',
    batchProgress: 'DSA 批次分析：{{completed}}/{{total}} 已完成',
    failedCount: '，{{count}} 個失敗',
    batchSubmitted: '批次分析已提交',
    summarizingReports: '正在彙總個股報告',
    allAnalysisCompleted: '已完成 AlphaSift 篩選和 {{count}} 隻股票的 DSA 分析',
    analysisPending: '分析等待中 - {{taskId}}',
    researchQuestion: '請綜合財務品質、競爭優勢、估值、主要風險和近期催化劑，提供可驗證的深度研究摘要。',
    statusPending: '等待中',
    statusProcessing: '處理中',
    statusCompleted: '已完成',
    statusFailed: '失敗',
  },
  de: {
    pageDescription: 'Marktübergreifendes Screening und Aktienresearch mit daily_stock_analysis und AlphaSift.',
    analyzeStock: 'Aktie analysieren',
    screenStocks: 'Aktien filtern',
    tickerPlaceholder: 'Ticker (z. B. 600519, AAPL)',
    aShares: 'A-Aktien',
    usMarket: 'US-Markt',
    analyzing: 'Analyse läuft...',
    analyze: 'Analysieren',
    runLabel: 'Lauf',
    analysisResult: 'Analyseergebnis',
    screenCriteriaPlaceholder: 'Filterkriterien beschreiben',
    screening: 'Filterung läuft...',
    screeningResults: 'Filterergebnisse',
    researchDisclaimer: 'Screening-Ergebnisse sind nur Forschungskandidaten und keine Anlageberatung. Prüfen Sie Quellenwarnungen und den vollständigen Bericht.',
    dsaService: 'DSA-Dienst',
    alphaSiftService: 'AlphaSift',
    ready: 'bereit',
    unavailable: 'nicht verfügbar',
    unknown: 'unbekannt',
    reportBrief: 'DSA Kurzbericht',
    reportDetailed: 'DSA Detailbericht',
    reportFull: 'DSA Vollbericht',
    working: 'In Bearbeitung...',
    scanMarket: 'Markt scannen',
    autoAnalyzeCandidates: 'Jeden AlphaSift-Kandidaten automatisch mit daily_stock_analysis analysieren',
    noMarketStrategy: 'Keine installierte AlphaSift-Strategie unterstützt diesen Markt. Starten Sie den Sidecar mit dem Hanggent-Launcher.',
    dataSourceDiagnostics: 'Datenquellen-Diagnose',
    dsaReport: 'daily_stock_analysis-Bericht',
    deepResearch: 'Tiefenanalyse',
    alphaDisclaimer: 'AlphaSift-Ergebnisse sind nur Forschungskandidaten zur weiteren Prüfung, keine Anlageberatung.',
    running: 'Läuft...',
    optionalContext: 'Optionaler Forschungskontext für AlphaSift',
    currentRun: 'Lauf {{runId}} - {{count}} Kandidaten',
    candidates: '{{count}} Kandidaten',
    evaluate: 'Auswerten',
    evaluation: 'Auswertung',
    savedRuns: 'Gespeicherte Läufe',
    noCandidates: 'Keine Kandidaten zurückgegeben.',
    rank: 'Rang',
    code: 'Code',
    name: 'Name',
    score: 'Punktzahl',
    price: 'Preis',
    change: 'Änderung',
    risk: 'Risiko',
    reason: 'Begründung',
    actions: 'Aktionen',
    analyzed: 'Analysiert',
    notAnalyzed: 'Nicht analysiert',
    rating: 'Punktzahl {{score}}',
    research: 'Recherchieren',
    batchProgress: 'DSA-Stapelanalyse: {{completed}}/{{total}} abgeschlossen',
    failedCount: ', {{count}} fehlgeschlagen',
    batchSubmitted: 'Stapelanalyse übermittelt',
    summarizingReports: 'Einzelberichte werden zusammengefasst',
    allAnalysisCompleted: 'AlphaSift-Screening und DSA-Analyse von {{count}} Aktien abgeschlossen',
    analysisPending: 'Analyse ausstehend - {{taskId}}',
    researchQuestion: 'Erstellen Sie eine überprüfbare Tiefenanalyse zu Finanzqualität, Wettbewerbsvorteilen, Bewertung, Hauptrisiken und kurzfristigen Katalysatoren.',
    statusPending: 'Ausstehend',
    statusProcessing: 'In Bearbeitung',
    statusCompleted: 'Abgeschlossen',
    statusFailed: 'Fehlgeschlagen',
  },
  ko: {
    pageDescription: 'daily_stock_analysis와 AlphaSift를 이용한 교차 시장 스크리닝 및 심층 종목 연구.',
    analyzeStock: '종목 분석',
    screenStocks: '종목 선별',
    tickerPlaceholder: '종목 코드 (예: 600519, AAPL)',
    aShares: '중국 A주',
    usMarket: '미국 시장',
    analyzing: '분석 중...',
    analyze: '분석',
    runLabel: '실행',
    analysisResult: '분석 결과',
    screenCriteriaPlaceholder: '선별 조건을 설명하세요',
    screening: '선별 중...',
    screeningResults: '선별 결과',
    researchDisclaimer: '선별 결과는 연구 후보일 뿐 투자 조언이 아닙니다. 결정 전에 데이터 경고와 전체 보고서를 확인하세요.',
    dsaService: 'DSA 서비스',
    alphaSiftService: 'AlphaSift',
    ready: '준비됨',
    unavailable: '사용 불가',
    unknown: '알 수 없음',
    reportBrief: 'DSA 요약',
    reportDetailed: 'DSA 상세',
    reportFull: 'DSA 전체',
    working: '처리 중...',
    scanMarket: '시장 스캔',
    autoAnalyzeCandidates: '모든 AlphaSift 후보를 daily_stock_analysis로 자동 분석',
    noMarketStrategy: '설치된 AlphaSift 전략이 이 시장을 지원하지 않습니다. Hanggent 실행기로 사이드카를 시작하세요.',
    dataSourceDiagnostics: '데이터 소스 진단',
    dsaReport: 'daily_stock_analysis 보고서',
    deepResearch: '심층 연구',
    alphaDisclaimer: 'AlphaSift 결과는 추가 검토를 위한 연구 후보이며 투자 조언이 아닙니다.',
    running: '실행 중...',
    optionalContext: 'AlphaSift용 선택적 연구 배경',
    currentRun: '실행 {{runId}} - 후보 {{count}}개',
    candidates: '후보 {{count}}개',
    evaluate: '평가',
    evaluation: '평가 결과',
    savedRuns: '저장된 실행',
    noCandidates: '반환된 후보가 없습니다.',
    rank: '순위',
    code: '코드',
    name: '이름',
    score: '점수',
    price: '가격',
    change: '변동',
    risk: '위험',
    reason: '이유',
    actions: '작업',
    analyzed: '분석 완료',
    notAnalyzed: '미분석',
    rating: '점수 {{score}}',
    research: '연구',
    batchProgress: 'DSA 일괄 분석: {{completed}}/{{total}} 완료',
    failedCount: ', {{count}}개 실패',
    batchSubmitted: '일괄 분석 제출됨',
    summarizingReports: '개별 종목 보고서 요약 중',
    allAnalysisCompleted: 'AlphaSift 선별 및 {{count}}개 종목 DSA 분석 완료',
    analysisPending: '분석 대기 중 - {{taskId}}',
    researchQuestion: '재무 품질, 경쟁 우위, 가치 평가, 주요 위험 및 단기 촉매를 포함한 검증 가능한 심층 연구 요약을 제공하세요.',
    statusPending: '대기 중',
    statusProcessing: '처리 중',
    statusCompleted: '완료',
    statusFailed: '실패',
  },
  ja: {
    pageDescription: 'daily_stock_analysis と AlphaSift による市場横断スクリーニングと銘柄の詳細調査。',
    analyzeStock: '銘柄を分析',
    screenStocks: '銘柄を選別',
    tickerPlaceholder: '銘柄コード（例: 600519、AAPL）',
    aShares: '中国A株',
    usMarket: '米国市場',
    analyzing: '分析中...',
    analyze: '分析',
    runLabel: '実行',
    analysisResult: '分析結果',
    screenCriteriaPlaceholder: '選別条件を入力',
    screening: '選別中...',
    screeningResults: '選別結果',
    researchDisclaimer: 'スクリーニング結果は調査候補であり、投資助言ではありません。判断前に警告と完全なレポートを確認してください。',
    dsaService: 'DSAサービス',
    alphaSiftService: 'AlphaSift',
    ready: '準備完了',
    unavailable: '利用不可',
    unknown: '不明',
    reportBrief: 'DSA概要',
    reportDetailed: 'DSA詳細',
    reportFull: 'DSA完全版',
    working: '処理中...',
    scanMarket: '市場をスキャン',
    autoAnalyzeCandidates: 'すべてのAlphaSift候補をdaily_stock_analysisで自動分析',
    noMarketStrategy: 'この市場をサポートするAlphaSift戦略がありません。Hanggentランチャーでサイドカーを起動してください。',
    dataSourceDiagnostics: 'データソース診断',
    dsaReport: 'daily_stock_analysis レポート',
    deepResearch: '詳細調査',
    alphaDisclaimer: 'AlphaSiftの結果は追加確認用の調査候補であり、投資助言ではありません。',
    running: '実行中...',
    optionalContext: 'AlphaSift用の任意の調査コンテキスト',
    currentRun: '実行 {{runId}} - 候補 {{count}}件',
    candidates: '候補 {{count}}件',
    evaluate: '評価',
    evaluation: '評価結果',
    savedRuns: '保存済み実行',
    noCandidates: '候補は返されませんでした。',
    rank: '順位',
    code: 'コード',
    name: '名称',
    score: 'スコア',
    price: '価格',
    change: '変動',
    risk: 'リスク',
    reason: '理由',
    actions: '操作',
    analyzed: '分析済み',
    notAnalyzed: '未分析',
    rating: 'スコア {{score}}',
    research: '調査',
    batchProgress: 'DSA一括分析: {{completed}}/{{total}} 完了',
    failedCount: '、{{count}}件失敗',
    batchSubmitted: '一括分析を送信しました',
    summarizingReports: '個別銘柄レポートを集約中',
    allAnalysisCompleted: 'AlphaSift選別と{{count}}銘柄のDSA分析が完了',
    analysisPending: '分析待機中 - {{taskId}}',
    researchQuestion: '財務品質、競争優位性、評価、主要リスク、短期的な材料を含む検証可能な詳細調査要約を作成してください。',
    statusPending: '待機中',
    statusProcessing: '処理中',
    statusCompleted: '完了',
    statusFailed: '失敗',
  },
  fr: {
    pageDescription: 'Filtrage multi-marchés et recherche approfondie avec daily_stock_analysis et AlphaSift.',
    analyzeStock: 'Analyser une action',
    screenStocks: 'Filtrer les actions',
    tickerPlaceholder: 'Symbole (ex. 600519, AAPL)',
    aShares: 'Actions A',
    usMarket: 'Marché américain',
    analyzing: 'Analyse...',
    analyze: 'Analyser',
    runLabel: 'Exécution',
    analysisResult: "Résultat de l'analyse",
    screenCriteriaPlaceholder: 'Décrivez les critères de filtrage',
    screening: 'Filtrage...',
    screeningResults: 'Résultats du filtrage',
    researchDisclaimer: "Les résultats sont des pistes de recherche, pas des conseils en investissement. Vérifiez les avertissements et le rapport complet avant toute décision.",
    dsaService: 'Service DSA',
    alphaSiftService: 'AlphaSift',
    ready: 'prêt',
    unavailable: 'indisponible',
    unknown: 'inconnu',
    reportBrief: 'DSA résumé',
    reportDetailed: 'DSA détaillé',
    reportFull: 'DSA complet',
    working: 'Traitement...',
    scanMarket: 'Analyser le marché',
    autoAnalyzeCandidates: 'Analyser automatiquement chaque candidat AlphaSift avec daily_stock_analysis',
    noMarketStrategy: "Aucune stratégie AlphaSift installée ne prend en charge ce marché. Démarrez le service avec le lanceur Hanggent.",
    dataSourceDiagnostics: 'Diagnostic des sources',
    dsaReport: 'Rapport daily_stock_analysis',
    deepResearch: 'Recherche approfondie',
    alphaDisclaimer: "Les résultats AlphaSift sont des pistes de recherche à vérifier, pas des conseils en investissement.",
    running: 'Exécution...',
    optionalContext: 'Contexte de recherche facultatif pour AlphaSift',
    currentRun: 'Exécution {{runId}} - {{count}} candidats',
    candidates: '{{count}} candidats',
    evaluate: 'Évaluer',
    evaluation: 'Évaluation',
    savedRuns: 'Exécutions enregistrées',
    noCandidates: 'Aucun candidat renvoyé.',
    rank: 'Rang',
    code: 'Code',
    name: 'Nom',
    score: 'Score',
    price: 'Prix',
    change: 'Variation',
    risk: 'Risque',
    reason: 'Raison',
    actions: 'Actions',
    analyzed: 'Analysé',
    notAnalyzed: 'Non analysé',
    rating: 'Score {{score}}',
    research: 'Rechercher',
    batchProgress: 'Analyse DSA groupée : {{completed}}/{{total}} terminées',
    failedCount: ', {{count}} échecs',
    batchSubmitted: 'Analyse groupée envoyée',
    summarizingReports: 'Synthèse des rapports individuels',
    allAnalysisCompleted: 'Filtrage AlphaSift et analyse DSA de {{count}} actions terminés',
    analysisPending: 'Analyse en attente - {{taskId}}',
    researchQuestion: 'Fournissez une synthèse vérifiable couvrant la qualité financière, les avantages concurrentiels, la valorisation, les risques majeurs et les catalyseurs proches.',
    statusPending: 'En attente',
    statusProcessing: 'En cours',
    statusCompleted: 'Terminé',
    statusFailed: 'Échec',
  },
  ru: {
    pageDescription: 'Межрыночный скрининг и глубокий анализ акций с daily_stock_analysis и AlphaSift.',
    analyzeStock: 'Анализ акции',
    screenStocks: 'Отбор акций',
    tickerPlaceholder: 'Тикер (например, 600519, AAPL)',
    aShares: 'Акции A',
    usMarket: 'Рынок США',
    analyzing: 'Анализ...',
    analyze: 'Анализировать',
    runLabel: 'Запуск',
    analysisResult: 'Результат анализа',
    screenCriteriaPlaceholder: 'Опишите критерии отбора',
    screening: 'Отбор...',
    screeningResults: 'Результаты отбора',
    researchDisclaimer: 'Результаты скрининга являются исследовательскими кандидатами, а не инвестиционной рекомендацией. Проверьте предупреждения и полный отчет.',
    dsaService: 'Сервис DSA',
    alphaSiftService: 'AlphaSift',
    ready: 'готов',
    unavailable: 'недоступен',
    unknown: 'неизвестно',
    reportBrief: 'Краткий DSA',
    reportDetailed: 'Подробный DSA',
    reportFull: 'Полный DSA',
    working: 'Обработка...',
    scanMarket: 'Сканировать рынок',
    autoAnalyzeCandidates: 'Автоматически анализировать каждого кандидата AlphaSift через daily_stock_analysis',
    noMarketStrategy: 'Установленные стратегии AlphaSift не поддерживают этот рынок. Запустите sidecar через Hanggent.',
    dataSourceDiagnostics: 'Диагностика источников',
    dsaReport: 'Отчет daily_stock_analysis',
    deepResearch: 'Глубокое исследование',
    alphaDisclaimer: 'Результаты AlphaSift предназначены только для дальнейшего исследования и не являются инвестиционной рекомендацией.',
    running: 'Выполняется...',
    optionalContext: 'Необязательный контекст исследования для AlphaSift',
    currentRun: 'Запуск {{runId}} - кандидатов: {{count}}',
    candidates: 'Кандидатов: {{count}}',
    evaluate: 'Оценить',
    evaluation: 'Оценка',
    savedRuns: 'Сохраненные запуски',
    noCandidates: 'Кандидаты не найдены.',
    rank: 'Место',
    code: 'Код',
    name: 'Название',
    score: 'Оценка',
    price: 'Цена',
    change: 'Изменение',
    risk: 'Риск',
    reason: 'Причина',
    actions: 'Действия',
    analyzed: 'Проанализировано',
    notAnalyzed: 'Не анализировалось',
    rating: 'Оценка {{score}}',
    research: 'Исследовать',
    batchProgress: 'Пакетный анализ DSA: {{completed}}/{{total}} завершено',
    failedCount: ', ошибок: {{count}}',
    batchSubmitted: 'Пакетный анализ отправлен',
    summarizingReports: 'Обобщение отчетов по акциям',
    allAnalysisCompleted: 'Скрининг AlphaSift и DSA-анализ {{count}} акций завершены',
    analysisPending: 'Анализ ожидает - {{taskId}}',
    researchQuestion: 'Подготовьте проверяемое глубокое исследование финансового качества, конкурентных преимуществ, оценки, основных рисков и ближайших катализаторов.',
    statusPending: 'Ожидает',
    statusProcessing: 'Обработка',
    statusCompleted: 'Завершено',
    statusFailed: 'Ошибка',
  },
  it: {
    pageDescription: 'Screening tra mercati e ricerca approfondita con daily_stock_analysis e AlphaSift.',
    analyzeStock: 'Analizza titolo',
    screenStocks: 'Filtra titoli',
    tickerPlaceholder: 'Ticker (es. 600519, AAPL)',
    aShares: 'Azioni A',
    usMarket: 'Mercato USA',
    analyzing: 'Analisi...',
    analyze: 'Analizza',
    runLabel: 'Esecuzione',
    analysisResult: 'Risultato analisi',
    screenCriteriaPlaceholder: 'Descrivi i criteri di screening',
    screening: 'Screening...',
    screeningResults: 'Risultati screening',
    researchDisclaimer: 'I risultati sono candidati di ricerca, non consulenza finanziaria. Verifica gli avvisi sulle fonti e il rapporto completo.',
    dsaService: 'Servizio DSA',
    alphaSiftService: 'AlphaSift',
    ready: 'pronto',
    unavailable: 'non disponibile',
    unknown: 'sconosciuto',
    reportBrief: 'DSA breve',
    reportDetailed: 'DSA dettagliato',
    reportFull: 'DSA completo',
    working: 'Elaborazione...',
    scanMarket: 'Scansiona mercato',
    autoAnalyzeCandidates: 'Analizza automaticamente ogni candidato AlphaSift con daily_stock_analysis',
    noMarketStrategy: 'Nessuna strategia AlphaSift installata supporta questo mercato. Avvia il sidecar con Hanggent.',
    dataSourceDiagnostics: 'Diagnostica fonti dati',
    dsaReport: 'Rapporto daily_stock_analysis',
    deepResearch: 'Ricerca approfondita',
    alphaDisclaimer: 'I risultati AlphaSift sono candidati di ricerca da verificare, non consulenza finanziaria.',
    running: 'In esecuzione...',
    optionalContext: 'Contesto di ricerca facoltativo per AlphaSift',
    currentRun: 'Esecuzione {{runId}} - {{count}} candidati',
    candidates: '{{count}} candidati',
    evaluate: 'Valuta',
    evaluation: 'Valutazione',
    savedRuns: 'Esecuzioni salvate',
    noCandidates: 'Nessun candidato restituito.',
    rank: 'Posizione',
    code: 'Codice',
    name: 'Nome',
    score: 'Punteggio',
    price: 'Prezzo',
    change: 'Variazione',
    risk: 'Rischio',
    reason: 'Motivo',
    actions: 'Azioni',
    analyzed: 'Analizzato',
    notAnalyzed: 'Non analizzato',
    rating: 'Punteggio {{score}}',
    research: 'Ricerca',
    batchProgress: 'Analisi DSA in batch: {{completed}}/{{total}} completate',
    failedCount: ', {{count}} non riuscite',
    batchSubmitted: 'Analisi in batch inviata',
    summarizingReports: 'Riepilogo dei rapporti sui titoli',
    allAnalysisCompleted: 'Screening AlphaSift e analisi DSA di {{count}} titoli completati',
    analysisPending: 'Analisi in attesa - {{taskId}}',
    researchQuestion: 'Fornisci una sintesi verificabile su qualità finanziaria, vantaggi competitivi, valutazione, rischi principali e catalizzatori a breve termine.',
    statusPending: 'In attesa',
    statusProcessing: 'In elaborazione',
    statusCompleted: 'Completato',
    statusFailed: 'Non riuscito',
  },
  ar: {
    pageDescription: 'فحص متعدد الأسواق وبحث معمق للأسهم بواسطة daily_stock_analysis وAlphaSift.',
    analyzeStock: 'تحليل سهم',
    screenStocks: 'فحص الأسهم',
    tickerPlaceholder: 'رمز السهم (مثل 600519، AAPL)',
    aShares: 'أسهم A',
    usMarket: 'السوق الأمريكية',
    analyzing: 'جارٍ التحليل...',
    analyze: 'تحليل',
    runLabel: 'تشغيل',
    analysisResult: 'نتيجة التحليل',
    screenCriteriaPlaceholder: 'صف معايير الفحص',
    screening: 'جارٍ الفحص...',
    screeningResults: 'نتائج الفحص',
    researchDisclaimer: 'نتائج الفحص مرشحات للبحث وليست نصيحة استثمارية. تحقق من تحذيرات المصادر والتقرير الكامل قبل اتخاذ القرار.',
    dsaService: 'خدمة DSA',
    alphaSiftService: 'AlphaSift',
    ready: 'جاهز',
    unavailable: 'غير متاح',
    unknown: 'غير معروف',
    reportBrief: 'DSA موجز',
    reportDetailed: 'DSA مفصل',
    reportFull: 'DSA كامل',
    working: 'جارٍ العمل...',
    scanMarket: 'فحص السوق',
    autoAnalyzeCandidates: 'تحليل كل مرشح AlphaSift تلقائيًا باستخدام daily_stock_analysis',
    noMarketStrategy: 'لا تدعم أي استراتيجية AlphaSift مثبتة هذا السوق. شغّل الخدمة الجانبية عبر مشغل Hanggent.',
    dataSourceDiagnostics: 'تشخيص مصادر البيانات',
    dsaReport: 'تقرير daily_stock_analysis',
    deepResearch: 'بحث معمق',
    alphaDisclaimer: 'نتائج AlphaSift مرشحات للبحث والمراجعة فقط وليست نصيحة استثمارية.',
    running: 'جارٍ التشغيل...',
    optionalContext: 'سياق بحث اختياري لـ AlphaSift',
    currentRun: 'التشغيل {{runId}} - {{count}} مرشحًا',
    candidates: '{{count}} مرشحًا',
    evaluate: 'تقييم',
    evaluation: 'التقييم',
    savedRuns: 'عمليات التشغيل المحفوظة',
    noCandidates: 'لم يتم إرجاع مرشحين.',
    rank: 'الترتيب',
    code: 'الرمز',
    name: 'الاسم',
    score: 'الدرجة',
    price: 'السعر',
    change: 'التغير',
    risk: 'المخاطر',
    reason: 'السبب',
    actions: 'الإجراءات',
    analyzed: 'تم التحليل',
    notAnalyzed: 'لم يُحلل',
    rating: 'الدرجة {{score}}',
    research: 'بحث',
    batchProgress: 'تحليل DSA الدفعي: اكتمل {{completed}}/{{total}}',
    failedCount: '، فشل {{count}}',
    batchSubmitted: 'تم إرسال التحليل الدفعي',
    summarizingReports: 'جارٍ تلخيص تقارير الأسهم',
    allAnalysisCompleted: 'اكتمل فحص AlphaSift وتحليل DSA لعدد {{count}} من الأسهم',
    analysisPending: 'التحليل قيد الانتظار - {{taskId}}',
    researchQuestion: 'قدّم ملخص بحث معمق قابلًا للتحقق يغطي الجودة المالية والمزايا التنافسية والتقييم والمخاطر الرئيسية والمحفزات القريبة.',
    statusPending: 'قيد الانتظار',
    statusProcessing: 'قيد المعالجة',
    statusCompleted: 'مكتمل',
    statusFailed: 'فشل',
  },
  es: {
    pageDescription: 'Filtrado entre mercados e investigación profunda con daily_stock_analysis y AlphaSift.',
    analyzeStock: 'Analizar acción',
    screenStocks: 'Filtrar acciones',
    tickerPlaceholder: 'Símbolo (p. ej. 600519, AAPL)',
    aShares: 'Acciones A',
    usMarket: 'Mercado de EE. UU.',
    analyzing: 'Analizando...',
    analyze: 'Analizar',
    runLabel: 'Ejecución',
    analysisResult: 'Resultado del análisis',
    screenCriteriaPlaceholder: 'Describe los criterios de filtrado',
    screening: 'Filtrando...',
    screeningResults: 'Resultados del filtrado',
    researchDisclaimer: 'Los resultados son candidatos de investigación, no asesoramiento de inversión. Revisa las advertencias y el informe completo.',
    dsaService: 'Servicio DSA',
    alphaSiftService: 'AlphaSift',
    ready: 'listo',
    unavailable: 'no disponible',
    unknown: 'desconocido',
    reportBrief: 'DSA breve',
    reportDetailed: 'DSA detallado',
    reportFull: 'DSA completo',
    working: 'Procesando...',
    scanMarket: 'Explorar mercado',
    autoAnalyzeCandidates: 'Analizar automáticamente cada candidato de AlphaSift con daily_stock_analysis',
    noMarketStrategy: 'Ninguna estrategia AlphaSift instalada admite este mercado. Inicia el servicio con el lanzador de Hanggent.',
    dataSourceDiagnostics: 'Diagnóstico de fuentes',
    dsaReport: 'Informe de daily_stock_analysis',
    deepResearch: 'Investigación profunda',
    alphaDisclaimer: 'Los resultados de AlphaSift son candidatos para investigar y no constituyen asesoramiento de inversión.',
    running: 'Ejecutando...',
    optionalContext: 'Contexto de investigación opcional para AlphaSift',
    currentRun: 'Ejecución {{runId}} - {{count}} candidatos',
    candidates: '{{count}} candidatos',
    evaluate: 'Evaluar',
    evaluation: 'Evaluación',
    savedRuns: 'Ejecuciones guardadas',
    noCandidates: 'No se devolvieron candidatos.',
    rank: 'Posición',
    code: 'Código',
    name: 'Nombre',
    score: 'Puntuación',
    price: 'Precio',
    change: 'Cambio',
    risk: 'Riesgo',
    reason: 'Motivo',
    actions: 'Acciones',
    analyzed: 'Analizado',
    notAnalyzed: 'Sin analizar',
    rating: 'Puntuación {{score}}',
    research: 'Investigar',
    batchProgress: 'Análisis DSA por lotes: {{completed}}/{{total}} completados',
    failedCount: ', {{count}} fallidos',
    batchSubmitted: 'Análisis por lotes enviado',
    summarizingReports: 'Resumiendo informes individuales',
    allAnalysisCompleted: 'Filtrado AlphaSift y análisis DSA de {{count}} acciones completados',
    analysisPending: 'Análisis pendiente - {{taskId}}',
    researchQuestion: 'Ofrece un resumen verificable sobre calidad financiera, ventajas competitivas, valoración, riesgos principales y catalizadores próximos.',
    statusPending: 'Pendiente',
    statusProcessing: 'Procesando',
    statusCompleted: 'Completado',
    statusFailed: 'Fallido',
  },
  hi: {
    pageDescription: 'daily_stock_analysis और AlphaSift द्वारा कई बाजारों की स्क्रीनिंग और गहन स्टॉक शोध।',
    analyzeStock: 'स्टॉक विश्लेषण',
    screenStocks: 'स्टॉक स्क्रीन करें',
    tickerPlaceholder: 'टिकर (जैसे 600519, AAPL)',
    aShares: 'A-शेयर',
    usMarket: 'अमेरिकी बाजार',
    analyzing: 'विश्लेषण जारी...',
    analyze: 'विश्लेषण करें',
    runLabel: 'रन',
    analysisResult: 'विश्लेषण परिणाम',
    screenCriteriaPlaceholder: 'स्क्रीनिंग मानदंड बताएं',
    screening: 'स्क्रीनिंग जारी...',
    screeningResults: 'स्क्रीनिंग परिणाम',
    researchDisclaimer: 'स्क्रीनिंग परिणाम केवल शोध उम्मीदवार हैं, निवेश सलाह नहीं। निर्णय से पहले स्रोत चेतावनियां और पूरी रिपोर्ट जांचें।',
    dsaService: 'DSA सेवा',
    alphaSiftService: 'AlphaSift',
    ready: 'तैयार',
    unavailable: 'अनुपलब्ध',
    unknown: 'अज्ञात',
    reportBrief: 'DSA संक्षिप्त',
    reportDetailed: 'DSA विस्तृत',
    reportFull: 'DSA पूर्ण',
    working: 'कार्य जारी...',
    scanMarket: 'बाजार स्कैन करें',
    autoAnalyzeCandidates: 'हर AlphaSift उम्मीदवार का daily_stock_analysis से स्वतः विश्लेषण करें',
    noMarketStrategy: 'कोई स्थापित AlphaSift रणनीति इस बाजार का समर्थन नहीं करती। Hanggent लॉन्चर से साइडकार शुरू करें।',
    dataSourceDiagnostics: 'डेटा स्रोत निदान',
    dsaReport: 'daily_stock_analysis रिपोर्ट',
    deepResearch: 'गहन शोध',
    alphaDisclaimer: 'AlphaSift परिणाम आगे की समीक्षा के लिए शोध उम्मीदवार हैं, निवेश सलाह नहीं।',
    running: 'चल रहा है...',
    optionalContext: 'AlphaSift के लिए वैकल्पिक शोध संदर्भ',
    currentRun: 'रन {{runId}} - {{count}} उम्मीदवार',
    candidates: '{{count}} उम्मीदवार',
    evaluate: 'मूल्यांकन',
    evaluation: 'मूल्यांकन परिणाम',
    savedRuns: 'सहेजे गए रन',
    noCandidates: 'कोई उम्मीदवार नहीं मिला।',
    rank: 'रैंक',
    code: 'कोड',
    name: 'नाम',
    score: 'स्कोर',
    price: 'मूल्य',
    change: 'बदलाव',
    risk: 'जोखिम',
    reason: 'कारण',
    actions: 'कार्रवाई',
    analyzed: 'विश्लेषित',
    notAnalyzed: 'विश्लेषित नहीं',
    rating: 'स्कोर {{score}}',
    research: 'शोध',
    batchProgress: 'DSA बैच विश्लेषण: {{completed}}/{{total}} पूर्ण',
    failedCount: ', {{count}} विफल',
    batchSubmitted: 'बैच विश्लेषण भेजा गया',
    summarizingReports: 'व्यक्तिगत स्टॉक रिपोर्ट का सार बन रहा है',
    allAnalysisCompleted: 'AlphaSift स्क्रीनिंग और {{count}} स्टॉक का DSA विश्लेषण पूर्ण',
    analysisPending: 'विश्लेषण लंबित - {{taskId}}',
    researchQuestion: 'वित्तीय गुणवत्ता, प्रतिस्पर्धी लाभ, मूल्यांकन, प्रमुख जोखिम और निकट अवधि के उत्प्रेरकों पर सत्यापन योग्य गहन शोध सार दें।',
    statusPending: 'लंबित',
    statusProcessing: 'प्रक्रिया में',
    statusCompleted: 'पूर्ण',
    statusFailed: 'विफल',
  },
};

export function getDsaReportLanguage(language?: string): 'zh' | 'en' {
  return language?.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}
